import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"
import type { CogoElement } from "../core/types"
import { finiteNumber, readResolvedArray, recordValue, resolvedText, shorthandId } from "./property-value-utils"

export function StepperRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // JITRULE Task 2: authored children skip the recordValue() shorthand path
  // below, so lift them directly — same rule, single interpreter.
  const explicitChildren = (element.children ?? []).map(liftV4Props)
  const children = explicitChildren.length > 0
    ? explicitChildren
    : readResolvedArray(element, "steps", resolveSymbol).map((raw, index) => {
        const step = recordValue(raw)
        const id = String(step.id ?? shorthandId(element.id, "steps", index))
        const content = step.content
        const contentChildren: CogoElement[] = Array.isArray(content)
          ? content.map((entry, childIndex) => {
              const child = recordValue(entry)
              return {
                ...child,
                id: String(child.id ?? shorthandId(id, "content", childIndex)),
                type: String(child.type ?? "text"),
                text: child.text ?? child.label,
              } as unknown as CogoElement
            })
          : [{ id: `${id}__content_text`, type: "text", text: resolvedText(content ?? step.description, resolveSymbol) }]
        return {
          ...step,
          id,
          type: String(step.type ?? "container"),
          text: step.label ?? step.text,
          value: String(step.value ?? id),
          children: contentChildren,
        } as unknown as CogoElement
      })
  const directCurrent = (element as unknown as { currentStep?: unknown }).currentStep
  const resolvedCurrent = typeof directCurrent === "string" ? resolveSymbol(directCurrent) : directCurrent
  const matchedIndex = typeof resolvedCurrent === "string"
    ? children.findIndex((child) => child.id === resolvedCurrent || child.value === resolvedCurrent || (child as unknown as { step?: unknown }).step === resolvedCurrent)
    : -1
  const initialStep = matchedIndex >= 0
    ? matchedIndex
    : finiteNumber(resolvedCurrent ?? (element.options as { activeStep?: unknown } | undefined)?.activeStep, 0)

  const [current, setCurrent] = useBoundValue<number>(
    element.bind,
    resolveSymbol,
    initialStep,
    Number,
  )

  const handleStep = useCallback(
    (step: number) => {
      setCurrent(step)
      dispatchEvent(element.id, "change", { step })
    },
    [element.id, dispatchEvent, setCurrent],
  )

  return (
    <div
      className={cn("flex flex-col gap-4", element.className)}
      data-testid={`stepper-${element.id}`}
    >
      {/* Step indicator row */}
      <div className="flex flex-row items-center gap-0">
        {children.map((child, i) => {
          const label = resolvedText(child.label ?? child.text, resolveSymbol, `Step ${i + 1}`)
          return (
          <div key={child.id} className="flex flex-row items-center flex-1">
            <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-none cursor-pointer transition-colors",
                i < current
                  ? "bg-primary text-primary-foreground"
                  : i === current
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
              )}
              onClick={() => handleStep(i)}
              aria-label={`Go to ${label}`}
              aria-current={i === current ? "step" : undefined}
            >
              {i < current ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                i + 1
              )}
            </button>
            <span className="text-xs">{label}</span>
            </div>
            {i < children.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-px mx-2",
                  i < current ? "bg-primary" : "bg-border",
                )}
              />
            )}
          </div>
          )
        })}
      </div>
      {/* Active step content */}
      {current < children.length && (
        <ElementRenderer element={children[current]} />
      )}
    </div>
  )
}
